import { createAsyncThunk } from "@reduxjs/toolkit";

import type {
	TConversationTurnPagePayload,
	TTurnInfo,
} from "../../types/agent";

import { services } from "../../services";
import { getApiErrorMessage } from "../../utils/apiErrorUtils";
import { loadPaulTurnHistory } from "../../utils/paulTurnHistory";

export const getConversations = createAsyncThunk(
	"agent/getConversations",
	async (_, { rejectWithValue }) => {
		try {
			const response =
				await services.agentServiceInstance.getConversations();
			return response;
		} catch (error: any) {
			return rejectWithValue(
				getApiErrorMessage(
					error,
					"Paul conversations could not be loaded."
				)
			);
		}
	}
);

export const getConversationMessagesById = createAsyncThunk(
	"agent/getMessagesByConversationId",
	async (conversationId: string, { rejectWithValue }) => {
		try {
			const response =
				await services.agentServiceInstance.getConversationMessagesById(
					conversationId
				);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				getApiErrorMessage(
					error,
					"Paul could not load this conversation."
				)
			);
		}
	}
);

export const deleteConversation = createAsyncThunk(
	"agent/deleteConversation",
	async (conversationId: string, { rejectWithValue }) => {
		try {
			await services.agentServiceInstance.deleteConversation(
				conversationId
			);
			return conversationId;
		} catch (error: any) {
			return rejectWithValue(
				getApiErrorMessage(
					error,
					"Paul conversation could not be deleted."
				)
			);
		}
	}
);

export const updateConversationTitle = createAsyncThunk(
	"agent/updateConversationTitle",
	async (
		{
			conversationId,
			newTitle,
		}: { conversationId: string; newTitle: string },
		{ rejectWithValue }
	) => {
		try {
			const response =
				await services.agentServiceInstance.updateConversationTitle(
					conversationId,
					newTitle
				);
			return response;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);
export const submitFeedback = createAsyncThunk(
	"agent/submitFeedback",
	async (
		{
			convId,
			turnIdx,
			expectedResult,
			actualResult,
			msgLink,
		}: {
			convId: string;
			turnIdx: number;
			expectedResult: string;
			actualResult: string;
			msgLink: string;
		},
		{ rejectWithValue }
	) => {
		try {
			await services.agentServiceInstance.submitFeedback(
				convId,
				turnIdx,
				{
					expectedResult,
					actualResult,
					msgLink,
				}
			);
			return {
				turnIdx,
				reason: `Expected: ${expectedResult}, Actual: ${actualResult}`,
			};
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const forkConversation = createAsyncThunk(
	"agent/forkConversation",
	async (
		{
			sourceConvId,
			turnIdx,
		}: { sourceConvId: string; turnIdx: number },
		{ rejectWithValue }
	) => {
		try {
			const res =
				await services.agentServiceInstance.forkConversation(
					sourceConvId,
					turnIdx
				);
			return res;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);

export const getTurnInfo = createAsyncThunk(
	"agent/getTurnInfo",
	async (
		{
			conversationId,
			turnIdx,
		}: { conversationId: string; turnIdx: number },
		{ rejectWithValue }
	) => {
		try {
			const response =
				await services.agentServiceInstance.getTurnInfo(
					conversationId,
					turnIdx
				);
			return response;
		} catch (error: any) {
			return rejectWithValue(
				getApiErrorMessage(
					error,
					"Paul could not load this message."
				)
			);
		}
	}
);

export const getConversationTurnPage = createAsyncThunk(
	"agent/getConversationTurnPage",
	async (
		{
			conversationId,
			turnIndices,
			requestKind,
		}: {
			conversationId: string;
			turnIndices: Array<number>;
			requestKind: "initial" | "loadMore";
		},
		{ rejectWithValue }
	) => {
		if (turnIndices.length === 0) {
			return {
				conversationId,
				requestKind,
				turnIndices,
				turns: [] as Array<TTurnInfo>,
				failures: [],
			} satisfies TConversationTurnPagePayload;
		}

		try {
			const { turns, failures } = await loadPaulTurnHistory(
				{
					conversationId,
					turnIndices,
					loadTurn: async (turnIdx) =>
						await services.agentServiceInstance.getTurnInfo(
							conversationId,
							turnIdx
						),
				}
			);

			return {
				conversationId,
				requestKind,
				turnIndices,
				turns,
				failures,
			} satisfies TConversationTurnPagePayload;
		} catch (error: unknown) {
			return rejectWithValue(
				getApiErrorMessage(
					error,
					"Paul could not load this part of the conversation."
				)
			);
		}
	}
);

export const getAgentMeta = createAsyncThunk(
	"agent/getAgentMeta",
	async (_, { rejectWithValue }) => {
		try {
			const response =
				await services.agentServiceInstance.getAgentMeta();
			return response;
		} catch (error: any) {
			return rejectWithValue(error.data);
		}
	}
);
